<template>
  <ACard id="author-works" class="ed-works">
    <template #title>
      <div class="ed-works__head">
        <h5 class="ed-works__h">저작물 관리</h5>
        <ABtn kind="yellow" size="sm" icon="add" @click="showAddModal = true">저작물 추가</ABtn>
      </div>
    </template>

    <div class="ed-works__body">
      <div v-if="loading" class="ed-works__loading">
        <div class="p-spinner p-spinner--lg"></div>
        <p>로딩 중...</p>
      </div>

      <AEmpty
        v-else-if="textbooks.length === 0"
        title="등록된 저작물이 없습니다"
        sub="저작물 추가 버튼을 눌러 새로운 저작물을 등록해보세요."
        size="sm"
      />

      <ATable
        v-else
        :columns="cols"
        :rows="textbooks"
        row-key="textbook_idx"
      >
        <template #cell:title="{ row }">
          <div class="ed-works__title">
            <img v-if="row.cover_image_url" :src="row.cover_image_url" :alt="row.title" class="ed-works__cover" @error="handleImageError" />
            <div v-else class="ed-works__cover-fallback">
              <i class="material-icons-round">menu_book</i>
            </div>
            <div>
              <p class="ed-works__title-name">{{ row.title }}</p>
              <p class="ed-works__title-pub">{{ row.publisher }}</p>
            </div>
          </div>
        </template>
        <template #cell:barcode="{ row }">
          <code class="ed-works__code">{{ row.barcode }}</code>
        </template>
        <template #cell:royalty="{ row }">
          <div v-if="editingIdx === row.textbook_idx" class="ed-works__edit-rate">
            <input
              ref="royaltyInput"
              v-model.number="editingValue"
              type="number"
              class="p-input ed-works__rate-input"
              min="0"
              :max="editingMax"
              step="0.01"
              @keyup.enter="saveRoyalty(row.textbook_idx)"
              @keyup.esc="cancelEdit"
              @blur="saveRoyalty(row.textbook_idx)"
            />
            <span v-if="editingType === 'amount'" class="ed-works__rate-unit">
              {{ editingCurrency }} / copy
              <small v-if="editingMax !== null" class="ed-works__rate-max">
                (최대 {{ formatRoyaltyAmount(editingMax) }} {{ editingCurrency }}{{ editingCoAuthorNote }})
              </small>
            </span>
            <span v-else class="ed-works__rate-unit">
              % <small class="ed-works__rate-max">(최대 {{ editingMax }}%{{ editingCoAuthorNote }})</small>
            </span>
          </div>
          <button
            v-else
            type="button"
            class="ed-works__rate-btn"
            :class="{ 'is-zero': isRoyaltyUnset(row) }"
            @click="startEdit(row)"
          >
            {{ royaltyLabel(row) }}
            <i class="material-icons-round">edit</i>
          </button>
        </template>
        <template #cell:status="{ row }">
          <APill :kind="statusKind(row.status)">{{ getStatusLabel(row.status) }}</APill>
        </template>
        <template #cell:actions="{ row }">
          <ABtn kind="danger" size="sm" @click="confirmDelete(row)">삭제</ABtn>
        </template>
      </ATable>

      <div v-if="zeroRoyaltyCount > 0" class="ed-works__alert">
        <i class="material-icons-round">warning</i>
        <span>인세가 0인 저작물: <strong>{{ zeroRoyaltyCount }}건</strong> (설정 필요)</span>
      </div>
    </div>

    <AddTextbookModal
      :show="showAddModal"
      :editor-idx="editorIdx"
      @close="showAddModal = false"
      @added="onTextbookAdded"
    />
  </ACard>
</template>

<script>
import ACard from "@/components/Pineapple/ACard.vue";
import ABtn from "@/components/Pineapple/ABtn.vue";
import APill from "@/components/Pineapple/APill.vue";
import ATable from "@/components/Pineapple/ATable.vue";
import AEmpty from "@/components/Pineapple/AEmpty.vue";
import AddTextbookModal from "./AddTextbookModal.vue";
import swal from "sweetalert2";

export default {
  name: "EditorWorksCard",
  components: { ACard, ABtn, APill, ATable, AEmpty, AddTextbookModal },
  props: {
    editorIdx: {
      type: [Number, String],
      required: true,
    },
  },
  emits: ['textbook-added', 'textbook-updated', 'textbook-deleted'],
  data() {
    return {
      textbooks: [],
      loading: false,
      editingIdx: null,
      editingType: 'rate',   // 편집 중인 도서의 인세 방식 (rate | amount)
      editingValue: null,    // rate 면 인세율(%), amount 면 1copy당 금액
      editingMax: null,      // 방식에 맞는 상한. null 이면 상한을 몰라 서버 검증에 맡깁니다
      editingCurrency: '',
      editingOthersSum: 0,   // 공저일 때 다른 저자들이 이미 차지한 몫 (0 이면 단독 저자)
      showAddModal: false,
      cols: [
        { key: 'title',   label: '교재명' },
        { key: 'barcode', label: '바코드', width: 160 },
        { key: 'royalty', label: '인세', width: 200 },
        { key: 'status',  label: '상태', width: 90 },
        { key: 'actions', label: '관리', width: 80, align: 'center' },
      ],
    };
  },
  computed: {
    // 정액 도서의 0원도 "인세 미설정"입니다 — 방식에 맞는 값으로 세어야 경고가 말이 됩니다.
    zeroRoyaltyCount() {
      return this.textbooks.filter((t) => this.isRoyaltyUnset(t)).length;
    },
    // 상한이 도서 상한보다 낮은 이유(=공저)를 밝혀야 관리자가 값을 오해하지 않습니다.
    editingCoAuthorNote() {
      if (!this.editingOthersSum) return '';
      const others = this.editingType === 'amount'
        ? `${this.formatRoyaltyAmount(this.editingOthersSum)} ${this.editingCurrency}`.trim()
        : `${this.editingOthersSum}%`;
      return ` · 다른 저자 ${others} 차감`;
    },
  },
  watch: {
    editorIdx: {
      handler(newVal) {
        if (newVal) this.loadTextbooks();
      },
      immediate: true,
    },
  },
  methods: {
    async loadTextbooks() {
      if (!this.editorIdx) return;
      this.loading = true;
      try {
        const res = await this.$axios({
          method: "get",
          url: `/textbook/editor/work/${this.editorIdx}/textbooks`,
          loadingBar: false,
        });
        if (res.status === 200) {
          this.textbooks = res.data || [];
        }
      } catch (error) {
        console.error("저작물 목록 로드 실패:", error);
        this.textbooks = [];
      } finally {
        this.loading = false;
      }
    },

    startEdit(textbook) {
      const isAmount = this.isAmountBook(textbook);
      this.editingIdx = textbook.textbook_idx;
      this.editingType = isAmount ? 'amount' : 'rate';
      this.editingValue = this.toNumber(this.royaltyOf(textbook)) ?? 0;
      this.editingCurrency = textbook.max_royalty_currency || '';
      // 공저 도서에서는 도서 상한이 아니라 "이 저자가 넣을 수 있는 최대"가 상한입니다(가이드 §9.5.2).
      // 상한 35%·저자 A 30% 면 저자 B 의 remaining 은 5 이고, 35 를 걸면 합계 초과 400 을 부릅니다.
      // remaining 이 없으면(개편 미배포) 종전처럼 도서 상한으로 두고 최종 검증은 서버에 맡깁니다.
      this.editingMax = isAmount
        ? (this.toNumber(textbook.remaining_royalty_amount)
            ?? this.toNumber(textbook.max_royalty_amount)
            ?? null)
        : (this.toNumber(textbook.remaining_royalty_rate)
            ?? this.toNumber(textbook.max_royalty_rate)
            ?? 100);
      this.editingOthersSum = isAmount
        ? (this.toNumber(textbook.others_royalty_amount_sum) ?? 0)
        : (this.toNumber(textbook.others_royalty_rate_sum) ?? 0);
      this.$nextTick(() => {
        const input = this.$refs.royaltyInput;
        if (input && input[0]) {
          input[0].focus();
          input[0].select();
        } else if (input && input.focus) {
          input.focus();
          input.select();
        }
      });
    },

    cancelEdit() {
      this.editingIdx = null;
      this.editingType = 'rate';
      this.editingValue = null;
      this.editingMax = null;
      this.editingCurrency = '';
      this.editingOthersSum = 0;
    },

    async saveRoyalty(textbookIdx) {
      // esc 로 취소하면 input 이 사라지면서 blur 가 한 번 더 들어옵니다.
      if (this.editingIdx !== textbookIdx) return;

      const isAmount = this.editingType === 'amount';
      const value = this.toNumber(this.editingValue);
      const max = this.editingMax;

      let invalidMsg = '';
      if (value === null || value < 0) {
        invalidMsg = isAmount
          ? '1copy당 인세금액은 0 이상이어야 합니다.'
          : '인세율은 0 이상이어야 합니다.';
      } else if (max !== null && value > max) {
        invalidMsg = isAmount
          ? `1copy당 인세금액은 0 이상 ${this.formatRoyaltyAmount(max)} 이하여야 합니다. (해당 도서의 최대 인세금액)`
          : `인세율은 0 이상 ${max} 이하여야 합니다. (해당 도서의 최대 인세율)`;
      }
      if (invalidMsg) {
        alert(invalidMsg);
        return;
      }

      const original = this.textbooks.find((t) => t.textbook_idx === textbookIdx);
      if (original && this.toNumber(this.royaltyOf(original)) === value) {
        this.cancelEdit();
        return;
      }
      try {
        // ★ 도서 방식이 요구하는 필드 "하나만" 실어야 합니다(가이드 §9.5.2). 두 필드 모두
        // Optional 이라 서버가 "안 보냄"과 "0 을 보냄"을 구분합니다 — amount 도서에
        // royalty_rate: 0 을 끼워 보내면 400 이므로 키 자체를 만들지 않습니다.
        const payload = isAmount ? { royalty_amount: value } : { royalty_rate: value };
        await this.$axios({
          method: "put",
          url: `/textbook/editor/work/${this.editorIdx}/textbooks/${textbookIdx}`,
          data: payload,
          loadingBar: true,
        });
        const idx = this.textbooks.findIndex((t) => t.textbook_idx === textbookIdx);
        if (idx !== -1) {
          this.textbooks[idx][isAmount ? 'royalty_amount' : 'royalty_rate'] = value;
        }
        this.$emit('textbook-updated');
      } catch (error) {
        console.error("인세 수정 실패:", error);
        alert(this.errorDetail(error, "인세 수정에 실패했습니다."));
      } finally {
        this.cancelEdit();
      }
    },

    // 이 삭제는 2026-08-28 부터 인세 연결 해제 + 편집권 회수를 겸합니다(가이드 §4).
    // 편집 권한이 textbook_editor 연결로 판정되므로, 이 행을 지우면 해당 저자는
    // 곧바로 자기 교재에서 403 을 맞습니다. 저자도 관리자도 "인세 행 하나 지운 것"과
    // 연결짓기 어려운 증상이라, 누르기 전에 결과를 문장으로 못박아 둡니다.
    async confirmDelete(textbook) {
      const confirmation = await swal.fire({
        title: '저작물을 삭제하시겠습니까?',
        html: `<b>${this.escapeHtml(textbook.title)}</b> 의 인세 설정을 삭제합니다.<br><br>`
          + '<b>이 저자는 해당 교재를 더 이상 편집할 수 없게 됩니다.</b><br>'
          + '(교재 편집 화면 진입 시 권한 오류가 발생합니다)',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: '삭제하고 편집권 회수',
        cancelButtonText: '취소',
        confirmButtonColor: '#d33',
      });
      if (!confirmation.isConfirmed) return;
      try {
        await this.$axios({
          method: "delete",
          url: `/textbook/editor/work/${this.editorIdx}/textbooks/${textbook.textbook_idx}`,
          loadingBar: true,
        });
        this.textbooks = this.textbooks.filter((t) => t.textbook_idx !== textbook.textbook_idx);
        alert("저작물이 삭제되었습니다. 이 저자의 해당 교재 편집 권한도 함께 회수되었습니다.");
        this.$emit('textbook-deleted');
      } catch (error) {
        console.error("저작물 삭제 실패:", error);
        alert(this.errorDetail(error, "저작물 삭제에 실패했습니다."));
      }
    },

    onTextbookAdded() {
      this.loadTextbooks();
      this.$emit('textbook-added');
    },

    handleImageError(event) {
      event.target.style.display = 'none';
    },

    // royalty_type 이 없는 응답(백엔드 미배포)은 기존과 같이 인세율 방식으로 봅니다.
    isAmountBook(textbook) {
      return textbook?.royalty_type === 'amount';
    },

    royaltyOf(textbook) {
      return this.isAmountBook(textbook) ? textbook?.royalty_amount : textbook?.royalty_rate;
    },

    isRoyaltyUnset(textbook) {
      return this.toNumber(this.royaltyOf(textbook)) === 0;
    },

    royaltyLabel(textbook) {
      if (!this.isAmountBook(textbook)) return `${this.formatRoyaltyRate(textbook.royalty_rate)}%`;
      const currency = textbook.max_royalty_currency ? ` ${textbook.max_royalty_currency}` : '';
      return `${this.formatRoyaltyAmount(textbook.royalty_amount)}${currency} / copy`;
    },

    // DECIMAL 은 문자열("10.00")로 올 수 있어 숫자로 맞춰 씁니다. 값이 없으면 null.
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const num = Number(value);
      return Number.isFinite(num) ? num : null;
    },

    formatRoyaltyRate(rate) {
      const num = this.toNumber(rate);
      return num === null ? '0.00' : num.toFixed(2);
    },

    // 정액은 "5 USD / copy" 처럼 읽혀야 해서 의미 없는 소수점은 버립니다.
    formatRoyaltyAmount(amount) {
      const num = this.toNumber(amount);
      if (num === null) return '0';
      return String(Number(num.toFixed(2)));
    },

    // 400 의 detail 은 그대로 노출할 한국어 문장입니다(§9.5.3).
    // 422 는 detail 이 배열이라 그대로 alert 하면 [object Object] 가 뜹니다.
    // 교재명은 저자가 입력한 값이라 그대로 html 에 넣으면 마크업이 살아납니다.
    escapeHtml(value) {
      return String(value ?? '').replace(/[&<>"']/g, (c) => (
        { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
      ));
    },

    errorDetail(error, fallback) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string' && detail) return detail;
      if (Array.isArray(detail)) {
        const msgs = detail.map((d) => d?.msg).filter(Boolean);
        if (msgs.length) return msgs.join('\n');
      }
      return fallback;
    },

    statusKind(status) {
      const map = { approval: 'ok', pending: 'warn', rejected: 'err' };
      return map[status] || 'default';
    },

    getStatusLabel(status) {
      const labels = { approval: '승인', pending: '대기', rejected: '거부' };
      return labels[status] || status;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/scss/pineapple";

.ed-works {
  &__head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }

  &__h {
    margin: 0;
    font-size: 14px;
    font-weight: 800;
    color: $p-ink;
  }

  &__body {
    max-height: 460px;
    overflow-y: auto;
  }

  &__loading {
    text-align: center;
    padding: 32px;

    p { margin: 12px 0 0; color: $p-ink-soft; font-size: 12px; }
  }

  &__title {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__cover, &__cover-fallback {
    width: 40px;
    height: 56px;
    border-radius: $p-radius-sm;
    overflow: hidden;
    flex-shrink: 0;
    border: 1px solid $p-line;
  }

  &__cover {
    object-fit: cover;
  }

  &__cover-fallback {
    background: $p-panel-alt;
    display: grid;
    place-items: center;
    color: $p-ink-soft;

    > i { font-size: 20px; }
  }

  &__title-name {
    margin: 0;
    font-size: 13px;
    font-weight: 700;
    color: $p-ink;
  }

  &__title-pub {
    margin: 2px 0 0;
    font-size: 11px;
    color: $p-ink-soft;
  }

  &__code {
    font-family: $p-font-mono;
    font-size: 11px;
    color: $p-point-deep;
    background: $p-point-soft;
    padding: 2px 6px;
    border-radius: 4px;
  }

  &__edit-rate {
    display: flex;
    align-items: center;
    gap: 4px;
    font-size: 12px;
  }

  &__rate-input {
    width: 70px;
    height: 30px;
    padding: 0 8px;
    font-size: 12px;
  }

  &__rate-unit {
    white-space: nowrap;
  }

  &__rate-max {
    font-size: 10px;
    color: $p-ink-soft;
    white-space: nowrap;
  }

  &__rate-btn {
    border: 0;
    background: transparent;
    color: $p-ink;
    font-family: $p-font-mono;
    font-size: 12px;
    font-weight: 700;
    padding: 4px 8px;
    border-radius: $p-radius;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: background $p-dur $p-ease;

    > i {
      font-size: 12px !important;
      opacity: 0;
      transition: opacity $p-dur $p-ease;
    }

    &:hover {
      background: $p-point-soft;
      color: $p-point-deep;

      > i { opacity: 1; }
    }

    &.is-zero {
      color: $p-err;
    }
  }

  &__alert {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 10px 14px;
    background: $p-warn-soft;
    border-left: 3px solid $p-warn;
    border-radius: $p-radius-md;
    font-size: 12px;
    color: $p-warn;

    > i { font-size: 16px; }
  }
}
</style>
